/**
 * Investor Score Detail Screen
 *
 * Shows detailed AI-powered score breakdown for a startup in the investor's dealflow.
 * Requires appropriate subscription tier to access.
 *
 * IMPORTANT: This is a DISPLAY-ONLY layer. All scoring logic runs server-side
 * in the `ai-score` / `score-startup` edge functions (`runScoring()` →
 * `score_event`). No formulas or weights here.
 */

import React, { useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { View, Text, ScrollView, ActivityIndicator, Pressable, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useRouter, useLocalSearchParams } from 'expo-router';
import {
  ChevronDown,
  ChevronLeft,
  AlertTriangle,
  Lock,
  Eye,
  Sliders,
  DollarSign,
  Info,
  ShieldCheck,
} from 'lucide-react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import type { ScoreEventReadModel } from '@/lib/supabase/edge-functions';
import { useInvestorLatestScore } from '@/lib/supabase/score-queries';
import { useRaisingStartups } from '@/lib/supabase/investor-queries';
import { RequestSourcesAccessCta } from '@/components/score/RequestSourcesAccessCta';
import { profileToScoreInput } from '@/lib/scoring/ai-scoring';
import { deckPreviewUrl } from '@/lib/investor/pitch-deck-analysis';
import { reportError } from '@/lib/errors';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { ValuationSection } from '@/components/valuation';
import { AiTransparencyLabel } from '@/components/ai-transparency-label';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { mode } from '@/lib/config/app-mode';

import { TierGate } from '@/components/subscription/TierGate';
import { FeatureLocked } from '@/components/subscription/FeatureLocked';
import { openPlans } from '@/lib/commerce/navigation';
import { COPY } from '@/lib/commerce/copy';
import { PlanLockedNotice } from '@/components/commerce/PlanLockedNotice';
import { ThesisFitCard, ThesisConfigSheet } from '@/components/thesis';
import {
  ExtractionOverviewCard,
  StructureCard,
  MetricsCard,
  PeopleCard,
  CompaniesCard,
  QualityGapsCard,
} from '@/components/investor/extraction';

import {
  IRSScoreCard,
  DimensionCard,
  ScoreSectionDivider,
  DealReadinessCard,
  ExecutiveSummarySection,
  ScoreRadarChart,
  DataRoomClaims,
} from '@/components/score';
import {
  scoreToLabel,
  computePillarSummaries,
  FUNDAMENTALS_DIMS,
  GROWTH_DIMS,
  evidenceLevelToLabel,
  evidenceLevelToSource,
  evidenceSourceMixFlavor,
  labelThemeColor,
} from '@/lib/scoring/score-display';

/** Collapsible section with header, chevron, and divider — used by extraction cards */
function CollapsibleSection({
  title,
  icon: Icon,
  badge,
  defaultOpen = true,
  colors,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ size: number; color: string; style?: object }>;
  badge?: ReactNode;
  defaultOpen?: boolean;
  colors: ReturnType<typeof useThemeColors>;
  children: ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const rotation = useSharedValue(defaultOpen ? 0 : -90);

  const toggle = useCallback(() => {
    setOpen(prev => {
      rotation.value = withTiming(!prev ? 0 : -90, { duration: 200 });
      return !prev;
    });
  }, [rotation]);

  const chevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  return (
    <View>
      {/* Divider */}
      <View style={{ height: 1, backgroundColor: colors.border.light, marginHorizontal: 20 }} />

      {/* Header */}
      <Pressable
        onPress={toggle}
        className="flex-row items-center px-5 py-3.5"
        style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
      >
        {Icon && <Icon size={16} color={colors.text.secondary} style={{ marginRight: 8 }} />}
        <Text style={{ color: colors.text.primary }} className="text-sm font-semibold flex-1" numberOfLines={1}>
          {title}
        </Text>
        {badge}
        <Animated.View style={chevronStyle}>
          <ChevronDown size={18} color={colors.text.tertiary} />
        </Animated.View>
      </Pressable>

      {/* Content */}
      {open && <View className="px-5 pb-4">{children}</View>}
    </View>
  );
}

// =============================================================================
// Data Inspector — raw extraction from PDF vs what scoring received
// =============================================================================

function DataInspector({
  startup,
  rawExtracted,
  richExtraction,
  aiResult,
  colors,
  dealId,
}: {
  startup: import('@/lib/supabase/edge-functions').StartupSummary;
  rawExtracted?: import('@/lib/supabase/edge-functions').ExtractedStartupData;
  richExtraction?: import('@/lib/supabase/edge-functions').RichExtraction;
  aiResult?: ScoreEventReadModel;
  colors: ReturnType<typeof useThemeColors>;
  dealId: string;
}) {
  const [activeTab, setActiveTab] = useState<'raw' | 'retained' | 'deck'>('raw');

  // profileToScoreInput expects the old StartupProfile shape — cast for now
  // until the scoring layer is migrated to StartupSummary
  const scoreInput = useMemo(() => profileToScoreInput(startup as any), [startup]);

  // The deck now lives in the `investor-decks` bucket rather than an in-memory
  // session cache, so the preview survives a reload and works on another
  // device. Keyed on the startup (durable) rather than the deal card (a client
  // object). The URL is short-lived and re-minted on mount.
  const [deckUrl, setDeckUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const startupId = startup?.id;
    if (!startupId) return;
    deckPreviewUrl(startupId)
      .then((url) => { if (!cancelled) setDeckUrl(url); })
      .catch((err) => reportError(err, { context: 'ScoreDetail.deckPreviewUrl' }));
    return () => { cancelled = true; };
  }, [startup?.id]);

  const tabs = [
    { key: 'raw' as const, label: 'Raw from PDF' },
    { key: 'retained' as const, label: 'Retained for Scoring' },
    { key: 'deck' as const, label: 'Deck Preview' },
  ];

  // Render an extracted field with value + confidence
  const renderExtractedField = (label: string, field: { value: unknown; confidence: number } | undefined) => {
    const hasValue = field && field.value !== undefined && field.value !== null && field.value !== '' &&
      field.value !== 0 && !(Array.isArray(field.value) && field.value.length === 0);
    const displayValue = !field ? '—  (field missing)'
      : !hasValue ? '—  (not found in deck)'
      : Array.isArray(field.value) ? field.value.join(', ')
      : String(field.value);

    return (
      <View key={label} className="py-2" style={{ borderBottomWidth: 0.5, borderBottomColor: colors.border.light }}>
        <View className="flex-row items-center gap-2 mb-0.5">
          <Text style={{ color: colors.text.tertiary }} className="text-xs font-semibold flex-1">
            {label}
          </Text>
          {field && (
            <View
              className="px-1.5 py-0.5 rounded"
              style={{ backgroundColor: field.confidence >= 70 ? colors.traffic.greenLight : field.confidence >= 40 ? colors.amber.light : colors.red.light }}
            >
              <Text
                style={{ color: field.confidence >= 70 ? colors.success : field.confidence >= 40 ? colors.warning : colors.error }}
                className="text-xs font-bold"
              >
                {field.confidence}%
              </Text>
            </View>
          )}
        </View>
        <Text
          style={{ color: hasValue ? colors.text.primary : colors.text.tertiary }}
          className={`text-xs ${hasValue ? '' : 'italic opacity-50'}`}
          selectable
        >
          {displayValue}
        </Text>
      </View>
    );
  };

  // Render a plain key/value for scoring input
  const renderField = (label: string, value: unknown) => {
    const isEmpty = value === undefined || value === null || value === '' || value === 0 ||
      (Array.isArray(value) && value.length === 0);
    const displayValue = isEmpty ? '—  (not passed)'
      : Array.isArray(value) ? value.join(', ')
      : typeof value === 'object' ? JSON.stringify(value, null, 2)
      : String(value);

    return (
      <View key={label} className="flex-row py-1.5" style={{ borderBottomWidth: 0.5, borderBottomColor: colors.border.light }}>
        <Text style={{ color: colors.text.tertiary }} className="text-xs w-36 font-medium">
          {label}
        </Text>
        <Text
          style={{ color: isEmpty ? colors.text.tertiary : colors.text.primary }}
          className={`text-xs flex-1 ${isEmpty ? 'italic opacity-50' : ''}`}
          selectable
        >
          {displayValue}
        </Text>
      </View>
    );
  };

  // Stats for raw extraction
  const rawStats = useMemo(() => {
    if (!rawExtracted) return null;
    const fields = Object.entries(rawExtracted).filter(([k]) => k !== 'overallConfidence');
    let populated = 0;
    let highConf = 0;
    for (const [, v] of fields) {
      if (typeof v === 'object' && v && 'value' in v) {
        const f = v as { value: unknown; confidence: number };
        const hasVal = f.value !== undefined && f.value !== null && f.value !== '' &&
          f.value !== 0 && !(Array.isArray(f.value) && f.value.length === 0);
        if (hasVal) populated++;
        if (f.confidence >= 70) highConf++;
      }
    }
    return { total: fields.length, populated, highConf, overallConfidence: rawExtracted.overallConfidence };
  }, [rawExtracted]);

  // Stats for scoring input
  const scoringStats = useMemo(() => {
    const entries = Object.entries(scoreInput.profileData);
    let populated = 0;
    for (const [, v] of entries) {
      if (v !== undefined && v !== null && v !== '' && v !== 0 && !(Array.isArray(v) && v.length === 0)) {
        populated++;
      }
    }
    return {
      total: entries.length,
      populated,
      hasPitchDeck: !!scoreInput.pitchDeckContent,
      hasDocs: (scoreInput.documentsContent?.length ?? 0) > 0,
    };
  }, [scoreInput]);

  return (
    <View className="gap-3">
      {/* Tab switcher */}
      <View className="flex-row rounded-lg overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
        {tabs.map(tab => (
          <Pressable
            key={tab.key}
            onPress={() => setActiveTab(tab.key)}
            className="flex-1 py-2.5 items-center"
            style={activeTab === tab.key ? { backgroundColor: colors.primary[600] } : undefined}
          >
            <Text
              style={{ color: activeTab === tab.key ? '#fff' : colors.text.secondary }}
              className="text-xs font-semibold"
            >
              {tab.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* ── Tab: Raw from PDF ── */}
      {activeTab === 'raw' && (
        <View className="gap-3">
          {/* Stats banner */}
          {richExtraction ? (
            <ExtractionOverviewCard extraction={richExtraction} />
          ) : rawStats ? (
            <View className="rounded-lg p-3" style={{ backgroundColor: colors.surface.secondary }}>
              <View className="flex-row items-center gap-3 mb-1.5">
                <Text style={{ color: colors.text.primary }} className="text-xs font-bold">
                  Overall confidence: {rawStats.overallConfidence}%
                </Text>
              </View>
              <Text style={{ color: colors.text.secondary }} className="text-xs">
                {rawStats.populated}/{rawStats.total} fields extracted  ·  {rawStats.highConf} high-confidence (70%+)
              </Text>
            </View>
          ) : (
            <View className="rounded-lg p-3" style={{ backgroundColor: colors.red.light }}>
              <Text style={{ color: colors.error }} className="text-xs font-semibold">
                Raw extraction data not stored for this startup
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
                Re-upload the pitch deck to capture the raw extraction. Older dealflow items were created before this data was persisted.
              </Text>
            </View>
          )}

          {/* Rich extraction data — component cards */}
          {richExtraction ? (
            <ScrollView style={{ maxHeight: 600 }} nestedScrollEnabled>
              <View className="gap-3">
                <StructureCard structure={richExtraction.structure} />

                {richExtraction.data.metrics.length > 0 && (
                  <MetricsCard metrics={richExtraction.data.metrics} />
                )}

                {richExtraction.entities.people.length > 0 && (
                  <PeopleCard people={richExtraction.entities.people} />
                )}

                {richExtraction.entities.companies.length > 0 && (
                  <CompaniesCard companies={richExtraction.entities.companies} />
                )}

                {(richExtraction.quality.gapsIdentified.length > 0 || richExtraction.quality.sectionConfidences.length > 0) && (
                  <QualityGapsCard quality={richExtraction.quality} />
                )}
              </View>
            </ScrollView>
          ) : rawExtracted ? (
            /* Legacy fallback: show flat extracted fields */
            <View className="rounded-xl p-3" style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}>
              <ScrollView style={{ maxHeight: 500 }} nestedScrollEnabled>
                {renderExtractedField('name', rawExtracted.name)}
                {renderExtractedField('tagline', rawExtracted.tagline)}
                {renderExtractedField('industry', rawExtracted.industry)}
                {renderExtractedField('stage', rawExtracted.stage)}
                {renderExtractedField('problem', rawExtracted.problem)}
                {renderExtractedField('solution', rawExtracted.solution)}
                {renderExtractedField('targetCustomer', rawExtracted.targetCustomer)}
                {renderExtractedField('marketSize', rawExtracted.marketSize)}
                {renderExtractedField('fundingTarget', rawExtracted.fundingTarget)}
                {renderExtractedField('teamSize', rawExtracted.teamSize)}
                {renderExtractedField('competitors', rawExtracted.competitors)}
                {renderExtractedField('location', rawExtracted.location)}
                {renderExtractedField('foundedYear', rawExtracted.foundedYear)}
                {renderExtractedField('monthlyRevenue', rawExtracted.monthlyRevenue)}
                {renderExtractedField('userCount', rawExtracted.userCount)}
                {renderExtractedField('growthRate', rawExtracted.growthRate)}
                {renderExtractedField('founderName', rawExtracted.founderName)}
                {renderExtractedField('founderEmail', rawExtracted.founderEmail)}
                {renderExtractedField('founderLinkedIn', rawExtracted.founderLinkedIn)}
                {renderExtractedField('website', rawExtracted.website)}
              </ScrollView>
            </View>
          ) : null}
        </View>
      )}

      {/* ── Tab: Retained for Scoring ── */}
      {activeTab === 'retained' && (
        <View className="gap-3">
          {/* Key insight banner */}
          <View className="rounded-lg p-3" style={{ backgroundColor: scoringStats.hasPitchDeck ? colors.traffic.greenLight : colors.red.light }}>
            <Text
              style={{ color: scoringStats.hasPitchDeck ? colors.success : colors.error }}
              className="text-xs font-bold mb-1"
            >
              {scoringStats.hasPitchDeck ? 'Full pitch deck text sent to scorer' : 'Pitch deck content NOT sent to scorer'}
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-xs">
              {scoringStats.hasPitchDeck
                ? 'The scoring LLM receives the full pitch deck alongside structured fields.'
                : 'The scoring LLM only sees these sparse structured fields — not the original slides. This causes shallow, generic analysis.'}
            </Text>
          </View>

          <View className="rounded-lg p-3" style={{ backgroundColor: colors.surface.secondary }}>
            <Text style={{ color: colors.text.secondary }} className="text-xs">
              {scoringStats.populated}/{scoringStats.total} profileData fields populated  ·
              pitchDeckContent: {scoringStats.hasPitchDeck ? 'yes' : 'no'}  ·
              documentsContent: {scoringStats.hasDocs ? 'yes' : 'no'}
            </Text>
          </View>

          {/* The actual scoring input */}
          <View className="rounded-xl p-3" style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}>
            <ScrollView style={{ maxHeight: 500 }} nestedScrollEnabled>
              {renderField('name', scoreInput.name)}
              {renderField('stage', scoreInput.stage)}
              {renderField('industry', scoreInput.industry)}
              {renderField('pitchDeckContent', scoreInput.pitchDeckContent ? `${scoreInput.pitchDeckContent.length} chars` : undefined)}
              {renderField('documentsContent', scoreInput.documentsContent)}

              <View className="mt-2 pt-2 border-t" style={{ borderColor: colors.border.light }}>
                <Text style={{ color: colors.text.tertiary }} className="text-xs font-semibold mb-2">
                  profileData sent to scorer:
                </Text>
                {Object.entries(scoreInput.profileData).map(([key, value]) => renderField(key, value))}
              </View>
            </ScrollView>
          </View>
        </View>
      )}

      {/* ── Tab: Deck Preview ── */}
      {activeTab === 'deck' && (
        <View className="gap-3">
          {!deckUrl ? (
            <View className="rounded-lg p-4" style={{ backgroundColor: colors.surface.secondary }}>
              <View className="flex-row items-center gap-2 mb-2">
                <Eye size={16} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold">
                  PDF not available
                </Text>
              </View>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                No stored deck for this company yet. Decks uploaded for analysis are kept privately and appear here.
              </Text>
            </View>
          ) : Platform.OS === 'web' ? (
            <View className="rounded-xl overflow-hidden" style={{ borderWidth: 1, borderColor: colors.border.light }}>
              {/* @ts-ignore - iframe is valid on web */}
              <iframe
                src={deckUrl}
                style={{ width: '100%', height: 500, border: 'none' }}
              />
            </View>
          ) : (
            <View className="rounded-lg p-4" style={{ backgroundColor: colors.surface.secondary }}>
              <View className="flex-row items-center gap-2 mb-2">
                <Eye size={16} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold">
                  Web only
                </Text>
              </View>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                PDF preview is only available on web. Open this page in a browser to see the deck.
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

export default function InvestorScoreDetailScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const { dealId } = useLocalSearchParams<{ dealId: string }>();

  // Get dealflow data and user role
  const dealflow = useDealflowStore((s) => s.dealflow);
  const userRole = useProfileStore((s) => s.userRole);
  const getCommitmentSummary = useDealflowStore((s) => s.getCommitmentSummary);
  // Raising-feed items are not in the store — resolve them from the feed
  // query (cache hit when navigating from dealflow/startup-detail; their
  // id IS the startup_profiles id).
  const { data: raisingFeed } = useRaisingStartups();
  const deal =
    dealflow.find((d) => d.id === dealId) ??
    raisingFeed?.find((d) => d.id === dealId || d.startup.id === dealId);
  const startup = deal?.startup;
  const commitmentSummary = startup ? getCommitmentSummary(startup.id, null) : null;
  const isActivelyRaising = !!startup?.funding?.currentRaise;

  // Score details available to all investor tiers — no subscription gate needed
  const hasAccess = true;

  // Breadcrumbs
  const breadcrumbs: Breadcrumb[] = useMemo(() => [
    { label: 'Dealflow', href: '/(investor)/dealflow' },
    { label: startup?.company.name || 'Startup', href: `/startup-detail?id=${dealId}` },
    { label: 'Score Details' },
  ], [startup?.company.name, dealId]);

  // ── DB-first data resolution (Phase 116.6 — ScoreEventReadModel) ──
  const startupId = startup?.id ?? deal?.id ?? '';
  const { data: dbScore, isLoading: isDbLoading, error: dbError } = useInvestorLatestScore(startupId); // projection: no raw score_event read for investors

  // Warm cache fallback from Zustand store
  const storeScore = deal?.scoreEvent ?? null;

  // DB-first, store-second
  const aiResult: ScoreEventReadModel | null = dbScore ?? storeScore;
  const isLoading = isDbLoading && !storeScore;
  const fetchError = !aiResult && dbError ? dbError : null;

  // ── Derived display data ──
  // True when neither a score_event nor a legacy readiness score exists —
  // rendered as an honest "not scored yet" state below, never a fake 0/100.
  const hasAnyScore = aiResult != null || deal?.readinessScore != null;
  const finalScore = aiResult?.composite.finalScore ?? deal?.readinessScore ?? 0;
  const ventureLabel = aiResult?.composite.ventureLabel ?? scoreToLabel(finalScore);
  const ventureScore = finalScore;
  // Confidence multiplier — derive from average per-dimension confidence (0-100 → 0-1).
  const confidenceMultiplier = useMemo(() => {
    const dims = aiResult?.dimensions ?? [];
    if (dims.length === 0) return 0;
    const sum = dims.reduce((s, d) => s + (d.confidence ?? 0), 0);
    return sum / dims.length / 100;
  }, [aiResult]);
  const evidenceLabel = evidenceLevelToLabel(
    confidenceMultiplier >= 0.7 ? 'verified' : confidenceMultiplier >= 0.4 ? 'partially-verified' : 'unverified',
  );
  const evidenceConfidence = Math.round(confidenceMultiplier * 100);
  const dimensions = aiResult?.dimensions ?? [];
  const sourceFlavor = useMemo(() => {
    if (!dimensions.length) return undefined;
    const sources = dimensions.map((d) => evidenceLevelToSource(d.evidenceLevel, !!d.feedback));
    return evidenceSourceMixFlavor(sources);
  }, [dimensions]);
  const risks = aiResult?.risks ?? [];
  const recommendations: string[] = [];

  // Risk summary string for IRSScoreCard
  const riskSummary = useMemo(() => {
    if (risks.length === 0) return 'None';
    const high = risks.filter(r => r.severity === 'high').length;
    const med = risks.filter(r => r.severity === 'medium').length;
    const parts: string[] = [];
    if (high > 0) parts.push(`${high} high`);
    if (med > 0) parts.push(`${med} medium`);
    if (parts.length === 0) parts.push(`${risks.length} low`);
    return parts.join(', ');
  }, [risks]);

  // Pillar grouping for dimension sections
  const pillarSummaries = useMemo(
    () => dimensions.length > 0 ? computePillarSummaries(dimensions) : null,
    [dimensions],
  );

  const fundamentalsDims = useMemo(
    () => dimensions.filter(d => (FUNDAMENTALS_DIMS as readonly string[]).includes(d.id)),
    [dimensions],
  );

  const growthDims = useMemo(
    () => dimensions.filter(d => (GROWTH_DIMS as readonly string[]).includes(d.id)),
    [dimensions],
  );

  // Build risk map: dimension → highest-severity risk for DD flags
  const dimRiskMap = useMemo(() => {
    const map = new Map<string, { description: string; severity: string; mitigation?: string }>();
    for (const risk of risks) {
      const existing = map.get(risk.category);
      if (!existing || risk.severity === 'high') {
        map.set(risk.category, risk);
      }
    }
    return map;
  }, [risks]);

  // Per-dimension risk count breakdown ({high, medium, low} per category).
  const dimRiskCountMap = useMemo(() => {
    const map = new Map<string, { high: number; medium: number; low: number }>();
    for (const risk of risks) {
      const cur = map.get(risk.category) ?? { high: 0, medium: 0, low: 0 };
      if (risk.severity === 'high') cur.high += 1;
      else if (risk.severity === 'medium') cur.medium += 1;
      else cur.low += 1;
      map.set(risk.category, cur);
    }
    return map;
  }, [risks]);

  // ── Thesis Fit ──
  const [showThesisConfig, setShowThesisConfig] = useState<boolean>(false);
  const dimensionScoreMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const d of dimensions) {
      map[d.id] = d.rawScore;
    }
    return map;
  }, [dimensions]);

  // Deal readiness — from deal-attractiveness dimension if present
  const dealDim = useMemo(
    () => dimensions.find(d => d.id === 'deal-attractiveness'),
    [dimensions],
  );

  if (!deal || !startup) {
    return (
      <AppLayout title="Score Details" breadcrumbs={breadcrumbs}>
        <View className="flex-1 items-center justify-center p-8">
          <Text style={{ color: colors.text.secondary }} numberOfLines={1}>Startup not found</Text>
          <AnimatedPressable
            onPress={() => router.back()}
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text className="text-white font-medium" numberOfLines={1}>Go Back</Text>
          </AnimatedPressable>
        </View>
      </AppLayout>
    );
  }

  if (!hasAccess) {
    const locked = COPY.scoreDetailLocked;
    return (
      <AppLayout title="Score Details" breadcrumbs={breadcrumbs}>
        {locked && openPlans ? (
          <View className="flex-1 items-center justify-center p-8">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-6"
              style={{ backgroundColor: colors.primary[100] }}
            >
              <Lock size={40} color={colors.primary[600]} />
            </View>
            <Text style={{ color: colors.text.primary }} className="text-xl font-bold text-center mb-2" numberOfLines={2}>
              {locked.title}
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-center mb-6 max-w-xs" numberOfLines={3}>
              {locked.body}
            </Text>
            <AnimatedPressable
              onPress={() => openPlans!(router, 'investor')}
              className="px-6 py-3 rounded-xl"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Text className="text-white font-semibold" numberOfLines={1}>{locked.cta}</Text>
            </AnimatedPressable>
          </View>
        ) : (
          <PlanLockedNotice variant="fullscreen" title="Detailed score breakdown" />
        )}
      </AppLayout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <AppLayout title="Score Details" breadcrumbs={breadcrumbs}>
        <View className="flex-1 items-center justify-center p-8">
          <ActivityIndicator size="large" color={colors.primary[600]} />
          <Text style={{ color: colors.text.secondary }} className="mt-4 text-sm" numberOfLines={1}>
            Computing score breakdown...
          </Text>
        </View>
      </AppLayout>
    );
  }

  // Honest empty state: loaded fine but the startup has never been scored.
  // Without this guard the page would render a fabricated 0/100 ("critical").
  if (!hasAnyScore) {
    return (
      <AppLayout title="Score Details" breadcrumbs={breadcrumbs}>
        <View className="flex-1 items-center justify-center p-8">
          <AlertTriangle size={40} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.primary }} className="text-lg font-bold text-center mt-4 mb-2" numberOfLines={2}>
            Not Scored Yet
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-center mb-6 max-w-xs" numberOfLines={3}>
            This startup doesn't have an Inverio Readiness Score yet. A score appears here once their profile or deck has been evaluated.
          </Text>
          <AnimatedPressable
            onPress={() => router.back()}
            className="px-6 py-3 rounded-xl"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text className="text-white font-semibold" numberOfLines={1}>Go Back</Text>
          </AnimatedPressable>
        </View>
      </AppLayout>
    );
  }

  // Error state (show basic info without breakdown)
  if (fetchError) {
    return (
      <AppLayout title="Score Details" breadcrumbs={breadcrumbs}>
        <View className="flex-1 items-center justify-center p-8">
          <AlertTriangle size={40} color={colors.warning} />
          <Text style={{ color: colors.text.primary }} className="text-lg font-bold text-center mt-4 mb-2" numberOfLines={2}>
            Score Breakdown Unavailable
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-center mb-4 max-w-xs" numberOfLines={3}>
            Unable to load detailed score breakdown. The overall score is {deal.readinessScore}/100.
          </Text>
          <View className="rounded-lg p-3 mb-6 max-w-xs" style={{ backgroundColor: colors.surface.secondary }}>
            <Text style={{ color: colors.text.tertiary }} className="text-xs text-center" numberOfLines={2}>
              {fetchError.message}
            </Text>
          </View>
          <AnimatedPressable
            onPress={() => router.back()}
            className="px-6 py-3 rounded-xl"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text className="text-white font-semibold" numberOfLines={1}>Go Back</Text>
          </AnimatedPressable>
        </View>
      </AppLayout>
    );
  }

  // ── Render dimension cards for a pillar ──
  const renderDimensionCards = (dims: typeof dimensions) => {
    const gridStyle = isWeb
      ? { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 12 }
      : { gap: 0 };
    const cardWrapStyle = isWeb ? { width: '48%' as const } : {};

    return (
      <View style={gridStyle}>
        {dims.map((dim) => {
          const matchingRisk = dimRiskMap.get(dim.id);
          const hasDDFlag = matchingRisk?.severity === 'high';
          return (
            <View key={dim.id} style={cardWrapStyle}>
              <DimensionCard
                name={dim.id}
                rawScore={dim.rawScore}
                feedback={dim.feedback ?? ''}
                evidenceLevel={dim.evidenceLevel}
                subScores={dim.subScores} claimsSummary={dim.claimsSummary}
                confidence={dim.confidence}
                riskLevel="low"
                riskCounts={
                  dimRiskCountMap.get(dim.id) ?? { high: 0, medium: 0, low: 0 }
                }
                dimensionRisk={matchingRisk}
                role="investor"
                hasDDFlag={hasDDFlag}
                ddFlagText={hasDDFlag ? matchingRisk?.description : undefined}
              />
            </View>
          );
        })}
      </View>
    );
  };

  return (
    <AppLayout title="Score Details" breadcrumbs={breadcrumbs}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1" style={{ backgroundColor: colors.surface.tertiary }}>
        {/* Mobile header with back button */}
        {phoneShell && (
          <View
            className="flex-row items-center px-4 py-3"
            style={{ backgroundColor: colors.surface.primary, borderBottomWidth: 1, borderBottomColor: colors.border.light }}
          >
            <Pressable
              onPress={() => {
                if (router.canGoBack()) {
                  router.back();
                } else {
                  router.replace('/(investor)/dealflow');
                }
              }}
              className="flex-row items-center gap-1 mr-3"
            >
              <ChevronLeft size={20} color={colors.primary[600]} />
              <Text style={{ color: colors.primary[600] }} className="text-sm font-medium" numberOfLines={1}>Back</Text>
            </Pressable>
            <Text style={{ color: colors.text.primary }} className="text-base font-semibold flex-1" numberOfLines={1}>
              Score Details
            </Text>
          </View>
        )}
        <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* 1. IRSScoreCard */}
        <View className="px-5 pt-4 pb-2">
          <IRSScoreCard
            companyName={startup.company.name}
            tagline={startup.company.tagline}
            description={startup.company.description}
            finalScore={finalScore}
            ventureLabel={ventureLabel}
            ventureScore={ventureScore}
            evidenceLabel={evidenceLabel}
            evidenceConfidence={evidenceConfidence}
            evidenceSourceFlavor={sourceFlavor}
            // Deal readiness reflects the VIEWED startup's data-room completeness, which
            // lives in that startup's own (user-scoped) data-room store and is not exposed
            // to investors via any RLS-visible query yet — so there is no real per-startup
            // source on this screen. We therefore show the illustrative sample (67% · 8/12)
            // ONLY in sample/demo mode and OMIT the row in production (undefined → "—")
            // rather than fabricate numbers. Follow-up: add a
            // `useStartupDealReadiness(startupId)` query backed by document_classifications
            // behind data-room-access RLS.
            dealReadinessPercent={mode.showSampleData ? 67 : undefined}
            dealReadinessPresent={mode.showSampleData ? 8 : undefined}
            dealReadinessTotal={mode.showSampleData ? 12 : undefined}
            riskSummary={riskSummary}
            commitmentSummary={commitmentSummary}
            isActivelyRaising={isActivelyRaising}
            stage={startup.company.stage}
          />
          {/* Proof material (quotes, sources, claims) follows the data-room grant. */}
          {aiResult?.rawInputsVisible === false && startupId ? (
            <RequestSourcesAccessCta
              startupId={startupId}
              dealId={deal?.source === 'raising' ? null : (deal?.id ?? null)}
              startupName={startup.company.name}
            />
          ) : null}
        </View>

        {/* 2. Executive Summary — investor 3-tab brief (thesis, signals, action) */}
        {dimensions.length > 0 && (
          <View className="px-5">
            <ScoreSectionDivider title="Executive Summary" label={ventureLabel} />
            <ExecutiveSummarySection
              companyName={startup.company.name}
              stage={startup.company.stage ?? 'pre-seed'}
              sector={startup.company.industry ?? 'Technology'}
              ventureLabel={ventureLabel}
              ventureScore={ventureScore}
              evidenceLabel={evidenceLabel}
              dimensions={dimensions}
              risks={risks}
              recommendations={recommendations}
            />
          </View>
        )}

        {/* 4. Venture Quality — two radar cards side-by-side */}
        {dimensions.length > 0 && pillarSummaries && (
          <View className="px-5">
            <ScoreSectionDivider
              title="Venture Quality"
              label={ventureLabel}
              score={ventureScore}
            />
            <View
              style={{
                flexDirection: isWeb ? 'row' : 'column',
                gap: 12,
                marginBottom: 4,
              }}
            >
              {/* Fundamentals radar */}
              <View
                style={{
                  flex: 1,
                  backgroundColor: colors.surface.primary,
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  padding: 14,
                  alignItems: 'center',
                }}
              >
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <Text
                    style={{
                      color: colors.text.tertiary,
                      fontSize: 9,
                      fontWeight: '700',
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                    }}
                  >
                    Fundamentals
                  </Text>
                  <Text
                    style={{
                      color: labelThemeColor(pillarSummaries.fundamentals.label, colors),
                      fontSize: 12,
                      fontWeight: '700',
                    }}
                  >
                    {pillarSummaries.fundamentals.averageScore}
                  </Text>
                </View>
                <ScoreRadarChart
                  size={isWeb ? 340 : 280}
                  showScoresInLabels
                  color={labelThemeColor(pillarSummaries.fundamentals.label, colors)}
                  dimensions={fundamentalsDims.map((d) => ({
                    name: d.id,
                    score: d.rawScore,
                  }))}
                />
              </View>

              {/* Growth & Execution radar */}
              <View
                style={{
                  flex: 1,
                  backgroundColor: colors.surface.primary,
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  padding: 14,
                  alignItems: 'center',
                }}
              >
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <Text
                    style={{
                      color: colors.text.tertiary,
                      fontSize: 9,
                      fontWeight: '700',
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                    }}
                  >
                    Growth & Execution
                  </Text>
                  <Text
                    style={{
                      color: labelThemeColor(pillarSummaries.growth.label, colors),
                      fontSize: 12,
                      fontWeight: '700',
                    }}
                  >
                    {pillarSummaries.growth.averageScore}
                  </Text>
                </View>
                <ScoreRadarChart
                  size={isWeb ? 340 : 280}
                  showScoresInLabels
                  color={labelThemeColor(pillarSummaries.growth.label, colors)}
                  dimensions={growthDims.map((d) => ({
                    name: d.id,
                    score: d.rawScore,
                  }))}
                />
              </View>
            </View>
          </View>
        )}

        {/* 5. Fundamentals — DimensionCards */}
        {fundamentalsDims.length > 0 && (
          <View className="px-5">
            <ScoreSectionDivider
              title="Fundamentals"
              label={pillarSummaries?.fundamentals.label}
              score={pillarSummaries?.fundamentals.averageScore}
            />
            {renderDimensionCards(fundamentalsDims)}
          </View>
        )}

        {/* 6. Growth & Execution — DimensionCards */}
        {growthDims.length > 0 && (
          <View className="px-5">
            <ScoreSectionDivider
              title="Growth & Execution"
              label={pillarSummaries?.growth.label}
              score={pillarSummaries?.growth.averageScore}
            />
            {renderDimensionCards(growthDims)}
          </View>
        )}

        {/* 6. Deal Readiness (conditional) */}
        {dealDim && dealDim.rawScore < 80 && (
          <View className="px-5">
            <ScoreSectionDivider title="Deal Readiness" />
            <DealReadinessCard
              present={Math.round(dealDim.rawScore / 10)}
              total={10}
              missingCritical={risks
                .filter(r => r.severity === 'high' && r.category === 'deal-attractiveness')
                .map(r => ({ name: r.description }))}
              missingRecommended={risks
                .filter(r => r.severity === 'medium' && r.category === 'deal-attractiveness')
                .map(r => ({ name: r.description }))}
            />
          </View>
        )}

        {/* ── Thesis Fit (investor-only, premium+) ── */}
        {userRole === 'investor' && dimensions.length > 0 && (
          <TierGate
            feature="thesis_fit"
            fallback={
              <CollapsibleSection title="Thesis Fit" icon={Sliders} defaultOpen={false} colors={colors}>
                <FeatureLocked feature="thesis_fit" variant="card" />
              </CollapsibleSection>
            }
          >
            <CollapsibleSection title="Thesis Fit" icon={Sliders} colors={colors}>
              <ThesisFitCard
                dimensionScores={dimensionScoreMap}
                inverioScore={finalScore}
                onConfigurePress={() => setShowThesisConfig(true)}
              />
            </CollapsibleSection>
          </TierGate>
        )}

        {/* ── Data-Room Claims (investor-only) ── */}
        {userRole === 'investor' && (
          <CollapsibleSection
            title="Data-Room Claims"
            icon={ShieldCheck}
            defaultOpen={false}
            colors={colors}
          >
            <DataRoomClaims startupId={startupId} role="investor" />
          </CollapsibleSection>
        )}

        {/* ── Extraction cards (preserved) ── */}
        <CollapsibleSection
          title="Valuation Analysis"
          icon={DollarSign}
          defaultOpen={false}
          colors={colors}
        >
          <ValuationSection startupId={dealId!} startup={{ name: startup.company.name, stage: startup.company.stage, industry: startup.company.industry }} />
        </CollapsibleSection>

        {/* ── Data Inspector (dev review) ── */}
        <CollapsibleSection
          title="Data Inspector"
          icon={Eye}
          defaultOpen={false}
          colors={colors}
          badge={
            <View className="px-2 py-0.5 rounded-full mr-2" style={{ backgroundColor: colors.amber.light }}>
              <Text style={{ color: colors.warning }} className="text-xs font-semibold" numberOfLines={1}>DEV</Text>
            </View>
          }
        >
          <DataInspector startup={startup} richExtraction={deal?.richExtraction} aiResult={deal?.scoreEvent} colors={colors} dealId={deal?.id ?? ''} />
        </CollapsibleSection>

        {/* AI Transparency footer */}
        <View className="px-5 py-4">
          <AiTransparencyLabel variant="banner" context="scoring" />
        </View>

        <View className="h-8" />
      </ScrollView>

      {/* Thesis Config overlay (inline, not a route) */}
      {userRole === 'investor' && (
        <ThesisConfigSheet
          visible={showThesisConfig}
          onClose={() => setShowThesisConfig(false)}
          investorId={deal?.id ?? ''}
        />
      )}
      </SafeAreaView>
    </AppLayout>
  );
}
